'use client';

import { useRouter, usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cookieName } from '@provider-directory/app/i18n/settings';
import { supportedLocales } from '@provider-directory/app/i18n/supported-locales';

export interface LanguageOption {
  label: string;
  value: string;
}

export const languages: LanguageOption[] = Array.from(
  supportedLocales.entries(),
).map(([locale, { display: label }]) => ({
  label,
  value: String(locale),
}));

interface UseLanguageHook {
  language: LanguageOption;
  setLocale: (value: LanguageOption) => void;
}

export const useLanguage = (): UseLanguageHook => {
  const { i18n } = useTranslation();
  const currentLocale = i18n.language;
  const router = useRouter();
  const pathname = usePathname();

  const [language, setLanguage] = useState(
    languages.find((lang) => lang.value === currentLocale) ?? languages[0],
  );

  const handleChange = (e: LanguageOption) => {
    const newLocale = e.value;

    // set cookie for detecting locale on SSR components
    const days = 30;
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = date.toUTCString();
    document.cookie = `${cookieName}=${newLocale};expires=${expires};path=/`;

    const url = new URL(window.location.href);
    if (newLocale !== 'en') {
      url.searchParams.set('l', newLocale);
    } else {
      url.searchParams.delete('l');
    }

    i18n.changeLanguage(newLocale);

    router.push(url.toString());
    setLanguage(e);
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    if (i18n.language !== 'en' && !url.searchParams.has('l')) {
      url.searchParams.set('l', currentLocale);
      router.push(url.toString(), { scroll: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, pathname]);

  return { language, setLocale: handleChange };
};
